import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, MousePointerClick, Search, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { adminService } from "@/pagesadmin/adminApi";
import { useToast } from "@/hooks/use-toast";

interface MarketingLinkRecord {
	id: number;
	phone_number: string;
	ip_address: string | null;
	created_at: string | null;
}

const PAGE_SIZE = 20;

const LinkClickAnalytics: React.FC = () => {
	const { toast } = useToast();

	const [records, setRecords] = useState<MarketingLinkRecord[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [loading, setLoading] = useState(false);

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const fetchRecords = useCallback(async (p: number, q: string) => {
		setLoading(true);
		try {
			const res = await adminService.listMarketingLinks({
				page: p,
				page_size: PAGE_SIZE,
				search: q || undefined,
			});
			setRecords(res.data.results);
			setTotal(res.data.total);
			setPage(p);
		} catch {
			toast({ title: "Failed to load marketing link records", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	}, [toast]);

	// Initial load
	useEffect(() => {
		fetchRecords(1, "");
	}, [fetchRecords]);

	const handleSearch = () => {
		setSearch(searchInput);
		fetchRecords(1, searchInput);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleSearch();
	};

	const handleClearSearch = () => {
		setSearchInput("");
		setSearch("");
		fetchRecords(1, "");
	};

	const formatDate = (iso: string | null) => {
		if (!iso) return "—";
		return new Date(iso).toLocaleString("en-IN", {
			timeZone: "Asia/Kolkata",
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		});
	};

	return (
		<div className="w-full space-y-4">
			{/* Header card */}
			<Card className="border border-gray-200 shadow-sm">
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between flex-wrap gap-3">
						<div className="flex items-center gap-2">
							<MousePointerClick className="h-5 w-5 text-intellend-green" />
							<CardTitle className="text-lg font-bold text-gray-900">
								Link Click Analytics
							</CardTitle>
							<Badge variant="secondary" className="font-mono text-xs ml-1">
								{total} total
							</Badge>
						</div>
						<Button
							variant="outline"
							size="sm"
							className="gap-1.5 text-xs border-intellend-green/40 text-intellend-green hover:bg-intellend-green/5 hover:border-intellend-green"
							onClick={() => fetchRecords(page, search)}
							disabled={loading}
						>
							<RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
							Refresh
						</Button>
					</div>
					<p className="text-sm text-gray-500 mt-1">
						Every visit to the Petpooja pre-approved marketing page where a visitor entered their mobile number.
					</p>
				</CardHeader>
				<CardContent className="pt-0">
					{/* Search bar */}
					<div className="flex gap-2 items-center">
						<div className="relative flex-1 max-w-sm">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
							<Input
								placeholder="Search by mobile or IP…"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								onKeyDown={handleKeyDown}
								className="pl-9 text-sm"
							/>
						</div>
						<Button
							size="sm"
							onClick={handleSearch}
							disabled={loading}
							className="bg-intellend-green hover:bg-intellend-green/90 text-white"
						>
							Search
						</Button>
						{search && (
							<Button
								size="sm"
								variant="outline"
								onClick={handleClearSearch}
								disabled={loading}
							>
								Clear
							</Button>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Table card */}
			<Card className="border border-gray-200 shadow-sm">
				<CardContent className="p-0">
					{loading ? (
						<div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-2">
							<Loader2 className="h-5 w-5 animate-spin" />
							Loading…
						</div>
					) : records.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-sm gap-2">
							<MousePointerClick className="h-8 w-8 opacity-30" />
							<span>No records found{search ? ` for "${search}"` : ""}.</span>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow className="bg-gray-50">
									<TableHead className="w-[70px] font-semibold text-gray-700">ID</TableHead>
									<TableHead className="font-semibold text-gray-700">Mobile</TableHead>
									<TableHead className="font-semibold text-gray-700">IP Address</TableHead>
									<TableHead className="font-semibold text-gray-700 text-right">Created On</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{records.map((r) => (
									<TableRow key={r.id} className="hover:bg-gray-50/60 transition-colors">
										<TableCell className="font-mono text-xs text-muted-foreground">
											{r.id}
										</TableCell>
										<TableCell className="font-mono font-medium text-gray-900">
											{r.phone_number}
										</TableCell>
										<TableCell className="font-mono text-sm text-gray-600">
											{r.ip_address ?? <span className="text-muted-foreground italic">—</span>}
										</TableCell>
										<TableCell className="text-right text-sm text-muted-foreground whitespace-nowrap">
											{formatDate(r.created_at)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* Pagination */}
			{total > PAGE_SIZE && (
				<div className="flex items-center justify-between px-1 text-sm">
					<span className="text-muted-foreground">
						Page {page} of {totalPages} &nbsp;·&nbsp; {total} records
					</span>
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={page <= 1 || loading}
							onClick={() => fetchRecords(page - 1, search)}
							className="gap-1"
						>
							<ChevronLeft className="h-4 w-4" />
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={page >= totalPages || loading}
							onClick={() => fetchRecords(page + 1, search)}
							className="gap-1"
						>
							Next
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default LinkClickAnalytics;
